/**
 * Bordereau Utility Functions
 */

import { comparePieceNumbers } from '@hector/piece-number-sort';
import type { RequestedFormat } from '../types/bordereau.types';

// ═══════════════════════════════════════════════════════════════════════════
// DATE EXTRACTION + AUTO-ORDERING (partagé step 1 wizard)
// ═══════════════════════════════════════════════════════════════════════════

const FRENCH_MONTHS: Record<string, number> = {
  janvier: 0, février: 1, mars: 2, avril: 3,
  mai: 4, juin: 5, juillet: 6, août: 7,
  septembre: 8, octobre: 9, novembre: 10, décembre: 11,
};

/**
 * Extrait une date d'un intitulé de pièce pour le tri chronologique.
 * Formats reconnus : `dd/mm/yyyy`, `dd-mm-yyyy`, `yyyy-mm-dd`, `12 janvier 2024`.
 * Retourne `null` si aucune date valide n'est trouvée.
 *
 * Source unique consommée par `NumberingStep` (bouton « Chronologique ») ET
 * l'auto-ordre à l'ouverture (`sortSelectedDocumentsForBordereau`) — mêmes
 * pièces triées de façon identique quelle que soit l'entrée.
 */
export function extractDateFromTitle(title: string): Date | null {
  if (!title) return null;
  const p1 = /(\d{1,2})[/-](\d{1,2})[/-](\d{4})/;
  const m1 = title.match(p1);
  if (m1) {
    const [, d = 0, m = 0, y = 0] = m1.map(Number);
    if (d >= 1 && d <= 31 && m - 1 >= 0 && m - 1 <= 11 && y >= 1900 && y <= 2100)
      return new Date(y, m - 1, d);
  }
  const p2 = /(\d{4})-(\d{1,2})-(\d{1,2})/;
  const m2 = title.match(p2);
  if (m2) {
    const [, y = 0, m = 0, d = 0] = m2.map(Number);
    if (d >= 1 && d <= 31 && m - 1 >= 0 && m - 1 <= 11 && y >= 1900 && y <= 2100)
      return new Date(y, m - 1, d);
  }
  const p3 = /(\d{1,2})\s+(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)\s+(\d{4})/i;
  const m3 = title.match(p3);
  if (m3) {
    const day = parseInt(m3[1] ?? '0', 10);
    const month = FRENCH_MONTHS[(m3[2] ?? '').toLowerCase()];
    const year = parseInt(m3[3] ?? '0', 10);
    if (month !== undefined && day >= 1 && day <= 31 && year >= 1900 && year <= 2100)
      return new Date(year, month, day);
  }
  return null;
}

/**
 * Auto-ordre canonique de la liste de pièces du wizard bordereau (step 1),
 * calqué sur la liste du dossier : les pièces **numérotées** d'abord, dans
 * l'ordre naturel du numéro (`2`, `2-1`, `2.1`, `3bis`, `3°4`… via le
 * comparateur partagé `@hector/piece-number-sort`), puis les pièces **sans
 * numéro** en ordre **chronologique** (date extraite de l'intitulé), les
 * non-datées en dernier (ordre d'origine préservé — `Array.sort` stable).
 *
 * Appliqué à l'ouverture du panel (`openPanel`) pour que l'avocat parte d'une
 * liste déjà ordonnée ; il reste libre de drag-reorder, trier chrono ou
 * renuméroter ensuite.
 *
 * @returns la liste des `id` dans l'ordre trié (pour alimenter `documentOrder`).
 */
export function sortSelectedDocumentsForBordereau(
  documents: Array<{ id: string; name: string; piece_number?: string | null }>,
): string[] {
  return [...documents]
    .sort((a, b) => {
      const numA = (a.piece_number ?? '').trim();
      const numB = (b.piece_number ?? '').trim();
      const hasA = numA.length > 0;
      const hasB = numB.length > 0;
      if (hasA && hasB) return comparePieceNumbers(numA, numB);
      if (hasA && !hasB) return -1; // numérotées avant non numérotées
      if (!hasA && hasB) return 1;
      // Ni l'une ni l'autre numérotée → chronologique, non-datées en dernier.
      const dA = extractDateFromTitle(a.name);
      const dB = extractDateFromTitle(b.name);
      if (!dA && !dB) return 0;
      if (!dA) return 1;
      if (!dB) return -1;
      return dA.getTime() - dB.getTime();
    })
    .map((d) => d.id);
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


/**
 * Truncates text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

// ═══════════════════════════════════════════════════════════════════════════
// IDENTITÉ D'UN EXPORT — quel livrable, sous quel nom
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Nom du sous-dossier qui porte les pièces tamponnées une à une, dans l'onglet
 * « Mes bordereaux ». C'est le SEUL livrable présenté comme un dossier : son
 * artefact est un ZIP de N fichiers, pas un document unique — l'ouvrir au
 * double-clic pour voir ce qu'il contient est plus juste qu'une ligne opaque.
 */
export const SEPARATE_PIECES_FOLDER_NAME = 'Pièces tamponnées séparées';

/**
 * Libellé du livrable, tel qu'il NOMME l'export enregistré (et donc le fichier
 * téléchargé, `bordereau-<name>.<ext>`).
 *
 * Volontairement différent de `FORMAT_LABEL` de `GenerationStep`, qui sert aux
 * toasts et à la bannière et porte le format entre parenthèses (« … (PDF) ») :
 * ici l'extension du fichier le dit déjà, et « (ZIP) » serait faux pour une
 * ligne qui s'affiche comme un sous-dossier.
 */
export const EXPORT_DELIVERABLE_LABEL: Record<RequestedFormat, string> = {
  pdf: 'Pièces tamponnées',
  pdf_with_cover: 'Bordereau + pièces tamponnées',
  zip: SEPARATE_PIECES_FOLDER_NAME,
};

/** Plafond de `bordereaux.name` (miroir de `EXPORT_NAME_MAX_LENGTH` backend). */
const EXPORT_NAME_MAX_LENGTH = 255;

/**
 * Nom sous lequel un export doit être enregistré : le livrable choisi, suivi du
 * nom du dossier. C'est ce que l'avocat lit dans « Mes bordereaux » et ce que
 * porte le fichier qu'il télécharge.
 *
 * Le titre du dossier est tronqué si besoin pour rester sous le plafond de la
 * colonne — le libellé du livrable, lui, n'est jamais rogné : c'est l'information
 * qui distingue deux exports du même dossier.
 */
export function buildExportName(format: RequestedFormat, caseTitle?: string | null): string {
  const label = EXPORT_DELIVERABLE_LABEL[format];
  const title = caseTitle?.trim();
  if (!title) return label;

  const separator = ' — ';
  const room = EXPORT_NAME_MAX_LENGTH - label.length - separator.length;
  if (room <= 0) return label;

  return `${label}${separator}${title.length > room ? truncateText(title, room) : title}`;
}

/**
 * Lit le livrable choisi dans `bordereaux.metadata.requested_format`.
 *
 * Rend `null` pour toute ligne générée avant cette option (le choix n'était
 * alors persisté nulle part) : l'appelant doit traiter ce cas comme « livrable
 * inconnu » et retomber sur le PDF, jamais supposer un format.
 */
export function readRequestedFormat(metadata: unknown): RequestedFormat | null {
  if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) return null;
  const raw = (metadata as Record<string, unknown>).requested_format;
  return raw === 'pdf' || raw === 'pdf_with_cover' || raw === 'zip' ? raw : null;
}

/**
 * Generates a default bordereau name
 */
export function generateBordereauName(): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).replace(/\//g, '-');
  return `Bordereau_${dateStr}`;
}

/**
 * Checks if a piece number is unique in the collection
 */
export function isNumberUnique(
  number: string,
  currentDocId: string,
  pieceNumbers: Record<string, { number: string }>
): boolean {
  return !Object.entries(pieceNumbers).some(
    ([docId, piece]) => docId !== currentDocId && piece.number === number
  );
}

/**
 * Sorts documents by their order index
 */
export function sortByOrder<T extends { id: string }>(
  items: T[],
  orderIds: string[]
): T[] {
  const orderMap = new Map(orderIds.map((id, index) => [id, index]));
  return [...items].sort((a, b) => {
    const orderA = orderMap.get(a.id) ?? Infinity;
    const orderB = orderMap.get(b.id) ?? Infinity;
    return orderA - orderB;
  });
}

/**
 * Detects duplicate piece numbers and returns details
 * Returns an object with duplicate numbers as keys and array of document names as values
 */
export function detectDuplicateNumbers(
  pieceNumbers: Record<string, { number: string; label?: string }>,
  documents: Array<{ id: string; name: string }>
): Record<string, string[]> | null {
  const numberToDocuments = new Map<string, string[]>();

  // Build map of number -> list of document names
  Object.entries(pieceNumbers).forEach(([docId, piece]) => {
    if (piece.number && piece.number.trim()) {
      const doc = documents.find(d => d.id === docId);
      const docName = doc?.name || 'Document inconnu';

      if (!numberToDocuments.has(piece.number)) {
        numberToDocuments.set(piece.number, []);
      }
      numberToDocuments.get(piece.number)!.push(docName);
    }
  });

  // Filter to only include numbers with duplicates
  const duplicates: Record<string, string[]> = {};
  let hasDuplicates = false;

  numberToDocuments.forEach((docNames, number) => {
    if (docNames.length > 1) {
      duplicates[number] = docNames;
      hasDuplicates = true;
    }
  });

  return hasDuplicates ? duplicates : null;
}
